import api from './api';
import { BlogPost, ApiResponse, PaginatedResponse } from '../types';

export interface Category {
  _id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBlogData {
  title: string;
  content: string;
  category: string; // category ID
  media?: string[]; // array of media URLs/IDs
}

export interface CreateCategoryData {
  name: string;
  description?: string;
}

const blogService = {
  // Blog Management APIs
  async createBlog(blogData: CreateBlogData): Promise<ApiResponse<BlogPost>> {
    const response = await api.post('/blog', blogData);
    return response.data;
  },

  async getAllBlogs(params?: {
    page?: number;
    limit?: number;
    sort?: string;
    order?: 'asc' | 'desc';
  }): Promise<ApiResponse<PaginatedResponse<BlogPost>>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.sort) queryParams.append('sort', params.sort);
    if (params?.order) queryParams.append('order', params.order);
    
    const response = await api.get(`/blog?${queryParams.toString()}`);
    return response.data;
  },
async getPublicBlogs(params?: {
    page?: number;
    limit?: number;
    sort?: string;
    order?: 'asc' | 'desc';
  }): Promise<ApiResponse<PaginatedResponse<BlogPost>>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.sort) queryParams.append('sort', params.sort);
    if (params?.order) queryParams.append('order', params.order);
    
    const response = await api.get(`/blog/published?${queryParams.toString()}`);
    return response.data;
  },
  async getMyBlogs(params?: {
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<PaginatedResponse<BlogPost>>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    
    const response = await api.get(`/blog/my?${queryParams.toString()}`);
    return response.data;
  },

  async getPublishedBlogs(params?: {
    page?: number;
    limit?: number;
    sort?: string;
    order?: 'asc' | 'desc';
  }): Promise<ApiResponse<PaginatedResponse<BlogPost>>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.sort) queryParams.append('sort', params.sort);
    if (params?.order) queryParams.append('order', params.order);
    
    const response = await api.get(`/blog/published?${queryParams.toString()}`);
    return response.data;
  },

  async getFeaturedBlogs(params?: {
    limit?: number;
  }): Promise<ApiResponse<PaginatedResponse<BlogPost>>> {
    const queryParams = new URLSearchParams();
    queryParams.append('isFeatured', 'true');
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    
    const response = await api.get(`/blog/published?${queryParams.toString()}`);
    return response.data;
  },

  async getBlogById(
    blogId: string, 
    params?: { commentsPage?: number; commentsLimit?: number }
  ): Promise<ApiResponse<BlogPost>> {
    const queryParams = new URLSearchParams();
    if (params?.commentsPage) queryParams.append('commentsPage', params.commentsPage.toString());
    if (params?.commentsLimit) queryParams.append('commentsLimit', params.commentsLimit.toString());
    
    const response = await api.get(`/blog/${blogId}?${queryParams.toString()}`);
    return response.data;
  },

  async updateBlog(blogId: string, blogData: Partial<CreateBlogData>): Promise<ApiResponse<BlogPost>> {
    const response = await api.put(`/blog/${blogId}`, blogData);
    return response.data;
  },

  async deleteBlog(blogId: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/blog/${blogId}`);
    return response.data;
  },

  async toggleBlogPublishStatus(blogId: string): Promise<ApiResponse<BlogPost>> {
    const response = await api.patch(`/blog/${blogId}/toggle-publish`);
    return response.data;
  },

  // Blog Like Management APIs
  async getBlogLikes(blogId: string): Promise<ApiResponse<{
    likesCount: number;
    userLiked: boolean;
    blogId: string;
  }>> {
    const response = await api.get(`/like/blog/${blogId}`);
    return response.data;
  },

  async toggleBlogLike(blogId: string): Promise<ApiResponse<{
    id: string;
    blogId: any;
    userId: any;
    isLiked: boolean;
    createdAt: string;
    updatedAt: string;
  }>> {
    const response = await api.post('/like/toggle', { blogId });
    return response.data;
  },

};

export { blogService };
export default blogService;
